import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Printer,
  Home,
  MapPin,
  Calendar,
  Clock,
  Users,
  Car,
  CreditCard,
  Mail,
  Phone,
  User,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";
import { formatInTimeZone } from "date-fns-tz";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";

const BookingConfirmation = () => {
  useSeo({ robots: "noindex,follow", title: "Booking Confirmed | Allen Travels RD" });
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as {
    booking: any;
    vehicle: { name: string; image_url: string | null };
  } | null;

  if (!state?.booking) {
    return (
      <div className="min-h-screen bg-gradient-soft flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center space-y-5 max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-gradient-ocean mx-auto flex items-center justify-center">
              <Car className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-2xl font-semibold text-foreground">No booking found</h1>
            <Button size="lg" onClick={() => navigate("/")} className="bg-gradient-ocean">Go Home</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const { booking, vehicle } = state;

  const paymentMethodLabels: Record<string, string> = {
    cash: "Cash",
    paypal: "PayPal",
    stripe: "Credit / Debit Card",
  };

  const statusVariant: Record<string, string> = {
    paid: "bg-primary/15 text-primary border-primary/30",
    confirmed: "bg-primary/15 text-primary border-primary/30",
    completed: "bg-primary/15 text-primary border-primary/30",
    pending: "bg-accent/15 text-accent border-accent/30",
    unpaid: "bg-accent/15 text-accent border-accent/30",
  };

  const voucherUrl = booking.booking_code
    ? `${window.location.origin}/voucher/${booking.booking_code}`
    : "";

  return (
    <div className="min-h-screen bg-gradient-soft flex flex-col">
      <div className="print:hidden"><Navbar /></div>

      {/* Hero confirmation */}
      <section className="relative pt-28 pb-12 overflow-hidden isolate print:hidden">
        <div className="absolute inset-0 z-0 bg-gradient-hero" />
        <div className="absolute inset-0 z-0 opacity-30 mix-blend-overlay bg-[radial-gradient(ellipse_at_top,white,transparent_60%)]" />
        <div className="relative z-10 container mx-auto px-4 text-white text-center">
          <motion.div
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
            className="w-20 h-20 mx-auto rounded-full bg-gradient-ocean shadow-glow flex items-center justify-center mb-6"
          >
            <CheckCircle2 className="w-10 h-10 text-white" />
          </motion.div>
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-xs font-medium tracking-[0.22em] uppercase">
            Booking Confirmed
          </span>
          <h1 className="font-display text-3xl md:text-5xl font-semibold mt-4 leading-tight">
            Your ride is <span className="text-gradient-sunset">locked in</span>
          </h1>
          <p className="mt-3 text-white/75 max-w-xl mx-auto">
            We've sent your voucher to your email. Save the booking code below.
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 pb-20 -mt-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl glass shadow-elegant overflow-hidden print:shadow-none print:border print:border-border"
        >
          {/* Booking Code header */}
          <div className="bg-gradient-hero px-6 py-6 text-center text-white">
            <p className="text-white/60 text-[10px] uppercase tracking-[0.22em] font-semibold mb-1">
              Booking Code
            </p>
            <p className="text-3xl md:text-4xl font-display font-semibold tracking-wider text-gradient-sunset">
              {booking.booking_code || "N/A"}
            </p>
          </div>

          <div className="p-7 md:p-8 space-y-7">
            {/* Route */}
            <section className="space-y-4">
              <h3 className="font-semibold text-[10px] uppercase tracking-[0.22em] text-primary">Route</h3>
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center pt-1">
                  <div className="w-3 h-3 rounded-full bg-primary" />
                  <div className="w-px h-10 bg-border" />
                  <div className="w-3 h-3 rounded-full bg-accent" />
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Pick-up</p>
                    <p className="text-foreground font-medium">{booking.pickup_location}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Drop-off</p>
                    <p className="text-foreground font-medium">{booking.dropoff_location}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <ArrowRight className="w-3 h-3" />
                {booking.route_type === "round-trip" ? "Round Trip" : "One Way"}
                {booking.distance_km && ` · ${booking.distance_km} km`}
                {booking.estimated_time_min && ` · ~${booking.estimated_time_min} min`}
              </div>
            </section>

            <hr className="border-border/60" />

            {/* Date / Vehicle / Pax */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <InfoBlock
                icon={<Calendar className="w-4 h-4 text-primary" />}
                label="Pickup Date"
                value={booking.pickup_date ? formatInTimeZone(new Date(booking.pickup_date), "UTC", "PPP 'at' p") : "N/A"}
              />
              {booking.return_date && (
                <InfoBlock
                  icon={<Calendar className="w-4 h-4 text-primary" />}
                  label="Return Date"
                  value={(() => { try { return formatInTimeZone(new Date(booking.return_date), "UTC", "PPP 'at' p"); } catch { return "N/A"; } })()}
                />
              )}
              <InfoBlock
                icon={<Car className="w-4 h-4 text-primary" />}
                label="Vehicle"
                value={vehicle.name}
              />
              <InfoBlock
                icon={<Users className="w-4 h-4 text-primary" />}
                label="Passengers"
                value={booking.passengers}
              />
              {booking.flight_number && (
                <InfoBlock
                  icon={<Clock className="w-4 h-4 text-primary" />}
                  label="Flight"
                  value={booking.flight_number}
                />
              )}
            </section>

            <hr className="border-border/60" />

            {/* Customer */}
            <section className="space-y-4">
              <h3 className="font-semibold text-[10px] uppercase tracking-[0.22em] text-primary">Customer</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-foreground truncate">{booking.customer_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-foreground truncate">{booking.customer_email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-foreground truncate">{booking.customer_phone}</span>
                </div>
              </div>
            </section>

            <hr className="border-border/60" />

            {/* Payment + total */}
            <section className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <CreditCard className="w-4 h-4 text-primary" />
                  <span className="text-muted-foreground">Payment:</span>
                  <span className="font-medium text-foreground">
                    {paymentMethodLabels[booking.payment_method] || booking.payment_method}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className={statusVariant[booking.payment_status] || statusVariant.pending}>
                    {booking.payment_status === "paid" ? "Paid" : booking.payment_status === "pending" ? "Pending" : "Unpaid"}
                  </Badge>
                  <Badge variant="outline" className={statusVariant[booking.status] || statusVariant.pending}>
                    {booking.status === "pending" ? "Pending" : booking.status}
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</p>
                <p className="text-4xl font-display font-semibold text-gradient-ocean">${booking.total_price}</p>
              </div>
            </section>

            {booking.special_requests && (
              <div className="bg-muted/60 rounded-xl p-4 border border-border/60">
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">
                  Special Requests
                </p>
                <p className="text-sm text-foreground">{booking.special_requests}</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* QR card */}
        {booking.booking_code && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="rounded-2xl glass shadow-card p-6 mt-6 text-center space-y-4 print:hidden"
          >
            <h3 className="font-semibold text-[10px] uppercase tracking-[0.22em] text-primary">
              Verification QR
            </h3>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(voucherUrl)}`}
              alt="Voucher QR Code"
              className="mx-auto rounded-xl border border-border bg-white p-2"
              width={180}
              height={180}
            />
            <a
              href={`/voucher/${booking.booking_code}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-medium"
            >
              View Public Voucher <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </motion.div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 print:hidden">
          <Button onClick={() => window.print()} variant="outline" className="flex-1 h-12">
            <Printer className="w-4 h-4 mr-2" /> Print Voucher
          </Button>
          <Button onClick={() => navigate("/")} className="flex-1 h-12 bg-gradient-ocean shadow-glow">
            <Home className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </div>

      <div className="print:hidden"><Footer /></div>
    </div>
  );
};

const InfoBlock = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-foreground truncate">{value}</p>
    </div>
  </div>
);

export default BookingConfirmation;
